import React, { useState, useEffect, useRef } from 'react';
import { Product, ProductCard } from './ProductCard';
import { getActiveProducts, FrontendProduct } from '../api/productApi';
import { Link } from 'react-router-dom';

const FeaturedProducts: React.FC = () => {
  const [products, setProducts] = useState<FrontendProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await getActiveProducts();
        
        // Filter products that EITHER have a collab OR price >= $500
        const featuredProducts = data.filter(product => 
          (product.collab && product.collab.trim() !== '') || 
          product.price >= 500
        );
        
        setProducts(featuredProducts);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load featured products.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);
  
  // No more debug logging here
  
  // Set up intersection observer for entrance animation
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(sectionRef.current);
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={`transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="mb-8">
        <h2 className="text-2xl font-bold">Featured Heat</h2>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {!loading && !error && products.length === 0 && (
        <div className="bg-background-light/20 rounded-lg p-8 text-center">
          <p className="text-secondary">No featured products available right now.</p>
          <p className="text-xs mt-2">Check back soon for new additions!</p>
        </div>
      )}

      {!loading && !error && products.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          
          {/* View All Heat button - matching the style of View All Inventory button */}
          <div 
            className="mt-16 text-center w-full"
            style={{ 
              transitionDelay: '600ms',
              transform: isVisible ? 'translateY(0)' : 'translateY(2rem)',
              opacity: isVisible ? 1 : 0,
              transition: 'all 700ms cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            <Link 
              to="/all-heat" 
              className="inline-block px-8 py-4 bg-accent/10 hover:bg-accent/20 border border-accent text-white rounded-md transition-all duration-300 font-medium group shadow-glow-sm"
            >
              <span className="flex items-center">
                View All Heat 
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </Link>
          </div>
        </>
      )}
    </section>
  );
};

export default FeaturedProducts; 